import { Controller } from "stimulus"
import { pageSessionHeaders, refreshCsrf, authenticationFailure } from "../session_recovery"

export default class extends Controller {
  static targets = ["dialog", "title", "description", "status", "notice", "stay", "leave", "login", "close"]

  connect() {
    this.deadline = Date.now() + Number(this.element.dataset.sessionActivityRemaining) * 1000
    this.absoluteDeadline = Date.now() + Number(this.element.dataset.sessionActivityAbsolute) * 1000
    this.lastRenewed = Date.now()
    this.lastChecked = 0
    this.activity = event => {
      if ((event.isTrusted || event.type === "session:activity") && !this.dialogTarget.open && !this.declined) this.lastActivity = Date.now()
    }
    this.events = ["keydown", "input", "pointerdown", "scroll", "session:activity"]
    this.events.forEach(name => document.addEventListener(name, this.activity, true))
    this.manualCheck = () => this.check()
    document.addEventListener("session:check", this.manualCheck)
    this.invalid = event => this.showExpired(event.detail.reason, event.detail.interactive)
    this.checked = event => this.acceptSession(event.detail)
    this.saved = () => {
      if (this.expired) return
      this.awaitingSave = false
      this.feedbackState = null
      this.noticeTarget.hidden = true
    }
    document.addEventListener("session:invalid", this.invalid)
    document.addEventListener("session:checked", this.checked)
    document.addEventListener("form:saved", this.saved, true)
    this.visibility = () => { if (!document.hidden) this.request(false) }
    document.addEventListener("visibilitychange", this.visibility)
    window.addEventListener("focus", this.visibility)
    this.timer = setInterval(() => this.tick(), 1000)
    this.request(false)
  }

  disconnect() {
    clearInterval(this.timer)
    this.events.forEach(name => document.removeEventListener(name, this.activity, true))
    document.removeEventListener("visibilitychange", this.visibility)
    window.removeEventListener("focus", this.visibility)
    document.removeEventListener("session:invalid", this.invalid)
    document.removeEventListener("session:checked", this.checked)
    document.removeEventListener("form:saved", this.saved, true)
    document.removeEventListener("session:check", this.manualCheck)
    if (this.abort) this.abort.abort()
    this.dialogTarget.close()
  }

  tick() {
    if (document.hidden) return
    if (this.expired) {
      if (Date.now() - this.lastChecked >= 30000) this.request(false)
      return
    }
    const remaining = this.deadline - Date.now()
    if (remaining <= 0) {
      // Another tab or a normal page request may have renewed the same session.
      this.request(false)
    } else if (Date.now() - this.lastActivity < 60000 && !this.declined && !this.dialogTarget.open && Date.now() - this.lastRenewed >= 60000) {
      this.request(true)
    } else if (remaining <= 60000 && !this.declined) {
      this.showWarning()
    }
    if (Date.now() - this.lastChecked >= 30000) this.request(false)
  }

  async request(renew, manual = false) {
    if (this.pending || (!manual && Date.now() < this.retryAfter)) return
    this.pending = true
    this.abort = new AbortController()
    const timeout = setTimeout(() => this.abort.abort(), 10000)
    this.stayTarget.disabled = true
    try {
      const response = await fetch(this.element.dataset.sessionActivityUrl, {
        method: renew ? "PATCH" : "GET",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        headers: { Accept: "application/json", ...pageSessionHeaders(), "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content },
        signal: this.abort.signal,
      })
      this.lastChecked = Date.now()
      if (response.status === 401) return this.showExpired()
      if (response.status === 429) {
        const retrySeconds = Number(response.headers.get("Retry-After"))
        this.retryAfter = Date.now() + Math.max(30, Number.isFinite(retrySeconds) ? retrySeconds : 30) * 1000
      }
      const data = await response.json().catch(() => null)
      if (authenticationFailure(response, data, false)) return
      if (!response.ok || !data) throw new Error("session request failed")
      this.acceptSession(data)
      this.retryAfter = 0
      this.deadline = Date.now() + data.remaining_seconds * 1000
      this.absoluteDeadline = Date.now() + data.absolute_remaining_seconds * 1000
      this.statusTarget.textContent = ""
      if (!this.awaitingSave) {
        this.noticeTarget.hidden = true
        if (this.feedbackState === "connection") this.feedback("healthy", "")
      }
      if (renew) {
        this.lastRenewed = Date.now()
        this.lastActivity = 0
        this.declined = false
        this.statusTarget.textContent = "Sesija pagarināta."
      }
      if (this.deadline - Date.now() > 60000) this.close()
    } catch (error) {
      this.lastChecked = Date.now()
      this.retryAfter = Math.max(this.retryAfter || 0, Date.now() + 30000)
      this.statusTarget.textContent = "Neizdevās pārbaudīt vai pagarināt sesiju. Pārbaudiet interneta savienojumu un mēģiniet vēlreiz."
      this.stayTarget.disabled = false
      if (!this.expired) this.feedback("connection", "Neizdevās pārbaudīt sesiju. Pārbaudiet interneta savienojumu un mēģiniet vēlreiz.")
      if (!this.expired && this.deadline - Date.now() <= 60000 && !this.declined) {
        this.showWarning()
      } else {
        this.noticeTarget.hidden = this.hasInlineFeedback
      }
    } finally {
      clearTimeout(timeout)
      this.pending = false
      this.stayTarget.disabled = false
    }
  }

  showWarning() {
    const absolute = this.absoluteDeadline - Date.now() <= 60000
    this.titleTarget.textContent = "Sesija drīz beigsies"
    this.descriptionTarget.textContent = absolute
      ? "Sasniegts šīs sesijas maksimālais ilgums. Saglabājiet izmaiņas un pieslēdzieties vēlreiz."
      : "Neaktivitātes dēļ sesija beigsies pēc nepilnas minūtes. Lai turpinātu darbu, nospiediet pogu “Turpināt darbu”."
    this.stayTarget.hidden = absolute
    this.loginTarget.hidden = !absolute
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
  }

  showExpired(reason = "session_expired", reopen = false) {
    const alreadyExpired = this.expired
    this.expired = true
    this.awaitingSave = false
    this.titleTarget.textContent = "Sesija ir beigusies"
    this.descriptionTarget.textContent = reason === "account_changed"
      ? "Citā cilnē ir pieslēgts cits konts vai mainīta konta drošība. Pieslēdzieties sākotnējā kontā. Ja mainīta konta drošība, pirms lapas pārlādes nokopējiet nesaglabāto tekstu. Izmaiņas nav saglabātas."
      : "Izmaiņas nav saglabātas. Pieslēdzieties vēlreiz tajā pašā kontā jaunā cilnē un atgriezieties šeit. Ievadītais paliek šajā lapā; pēc sesijas atjaunošanas nospiediet saglabāšanas pogu."
    this.statusTarget.textContent = ""
    const message = this.hasInlineFeedback && reason !== "account_changed"
      ? "Sesija ir beigusies. Ievadītais paliek šajā lapā. Pieslēdzieties vēlreiz tajā pašā kontā. Izmaiņas nav saglabātas."
      : `${this.titleTarget.textContent}. ${this.descriptionTarget.textContent}`
    this.feedback("expired", message)
    this.stayTarget.hidden = true
    this.leaveTarget.hidden = true
    this.loginTarget.hidden = false
    this.closeTarget.hidden = false
    if (this.hasInlineFeedback) return this.close()
    if ((!alreadyExpired || reopen) && !this.dialogTarget.open) this.dialogTarget.showModal()
  }

  acceptSession(data) {
    const recovered = this.expired
    refreshCsrf(data.csrf_token)
    this.expired = false
    this.deadline = Date.now() + data.remaining_seconds * 1000
    this.absoluteDeadline = Date.now() + data.absolute_remaining_seconds * 1000
    this.lastChecked = Date.now()
    this.stayTarget.hidden = false
    this.leaveTarget.hidden = false
    this.loginTarget.hidden = true
    this.closeTarget.hidden = true
    if (recovered) {
      this.declined = false
      this.lastActivity = 0
      this.close()
      this.awaitingSave = true
    }
    if (this.awaitingSave) {
      this.feedback("recovered", "Sesija atjaunota. Izmaiņas vēl nav saglabātas; nospiediet saglabāšanas pogu.")
    }
  }

  get hasInlineFeedback() {
    return !!document.querySelector('[data-session-feedback="report"]')
  }

  feedback(state, message) {
    const changed = this.feedbackState !== state || this.feedbackMessage !== message
    this.feedbackState = state
    this.feedbackMessage = message
    this.noticeTarget.textContent = message
    this.noticeTarget.hidden = this.hasInlineFeedback || state === "healthy"
    if (changed) document.dispatchEvent(new CustomEvent("session:feedback", { detail: { state, message } }))
  }

  check() {
    this.request(false, true)
  }

  stay() {
    this.request(true, true)
  }

  decline(event) {
    event.preventDefault()
    this.declined = true
    this.lastActivity = 0
    this.close()
  }

  close() {
    this.dialogTarget.close()
  }
}
